import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Clock, History, ListChecks, MapPin, UserCog, UserPlus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AppShell } from "@/components/AppShell";
import { api, fmtKLDateTime } from "@/lib/api";
import { useLang, reasonLabel } from "@/lib/i18n";

const PAGE_SIZE = 25;

const RoleBadge = ({ role, t }) => {
  const cls = {
    admin: "bg-primary/10 text-primary",
    supervisor: "bg-teal-50 text-teal-900",
    staff: "bg-secondary text-muted-foreground",
  }[role] || "bg-secondary text-muted-foreground";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {t(role)}
    </span>
  );
};

/* Role change log tab: who promoted whom and when (Batch 10). Sourced from
   the audit log so records survive user deletion. */
const RoleChangesTab = () => {
  const { t } = useLang();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ["role-changes", page],
    queryFn: async () => (await api.get("/admin/role-changes", {
      params: { page, page_size: PAGE_SIZE },
    })).data,
  });
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / PAGE_SIZE));
  const items = data?.items || [];

  if (isLoading) {
    return (
      <div className="space-y-2 pt-2">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 rounded-xl" />)}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <p data-testid="role-changes-empty"
        className="mt-2 rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
        {t("noRoleChanges")}
      </p>
    );
  }
  return (
    <div className="space-y-4 pt-2">
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <Table data-testid="role-changes-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t("when")}</TableHead>
              <TableHead>{t("changedBy")}</TableHead>
              <TableHead>{t("userCol")}</TableHead>
              <TableHead>{t("changeCol")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((r) => (
              <TableRow key={r.id} data-testid="role-change-row">
                <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums">
                  {fmtKLDateTime(r.at)}
                </TableCell>
                <TableCell className="font-medium">
                  {r.actorName}
                  {r.actorRole && (
                    <span className="ml-1 text-xs text-muted-foreground">({t(r.actorRole)})</span>
                  )}
                </TableCell>
                <TableCell>{r.userName}</TableCell>
                <TableCell>
                  {r.kind === "created" ? (
                    <span className="flex items-center gap-1.5 text-xs" data-testid="role-change-created">
                      <UserPlus className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">{t("createdAs")}</span>
                      <RoleBadge role={r.roleAfter} t={t} />
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-xs" data-testid="role-change-changed">
                      <UserCog className="h-3.5 w-3.5 text-muted-foreground" />
                      <RoleBadge role={r.roleBefore} t={t} />
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                      <RoleBadge role={r.roleAfter} t={t} />
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button size="sm" variant="outline" data-testid="role-changes-prev"
            disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            {t("prevPage")}
          </Button>
          <span className="text-xs tabular-nums text-muted-foreground">{page} / {totalPages}</span>
          <Button size="sm" variant="outline" data-testid="role-changes-next"
            disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            {t("nextPage")}
          </Button>
        </div>
      )}
    </div>
  );
};

const TypeBadge = ({ type, t }) => {
  const map = {
    time: { icon: Clock, label: t("typeTime"), cls: "bg-sky-50 text-sky-900" },
    location: { icon: MapPin, label: t("typeLocation"), cls: "bg-violet-50 text-violet-900" },
    review: { icon: ListChecks, label: t("typeReview"), cls: "bg-amber-50 text-amber-900" },
  };
  const m = map[type];
  if (!m) return null;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${m.cls}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
};

/* Admin-only oversight of every punch override/edit made by supervisors:
   time edits, location edits and review decisions — with the note/reason.
   Filterable by supervisor and by edit type (Batch 7). */
export default function AdminOverridesPage() {
  const { t } = useLang();
  const [actorId, setActorId] = useState("all");
  const [editType, setEditType] = useState("all");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["overrides", actorId, editType, page],
    queryFn: async () => (await api.get("/admin/overrides", {
      params: {
        actor_id: actorId === "all" ? "" : actorId,
        edit_type: editType === "all" ? "" : editType,
        page, page_size: PAGE_SIZE,
      },
    })).data,
  });

  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / PAGE_SIZE));
  const items = data?.items || [];

  const decisionBadge = (row) => {
    if (!row.decision) return null;
    const cls = {
      approved: "bg-emerald-50 text-emerald-900",
      flagged: "bg-rose-50 text-rose-900",
      overridden: "bg-amber-50 text-amber-900",
    }[row.decision];
    return <Badge variant="outline" className={`border-transparent ${cls}`}>{t(row.decision)}</Badge>;
  };

  return (
    <AppShell>
      <div className="space-y-4" data-testid="overrides-page">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
            <History className="h-5 w-5 text-primary" /> {t("overridesNav")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("overridesHelp")}</p>
        </div>

        <Tabs defaultValue="punch">
          <TabsList>
            <TabsTrigger value="punch" data-testid="tab-punch-edits">{t("punchEditsTab")}</TabsTrigger>
            <TabsTrigger value="roles" data-testid="tab-role-changes">{t("roleChangesTab")}</TabsTrigger>
          </TabsList>

          <TabsContent value="punch" className="space-y-4 pt-2">
        <div className="flex flex-wrap gap-2">
          <Select value={actorId} onValueChange={(v) => { setActorId(v); setPage(1); }}>
            <SelectTrigger data-testid="overrides-actor-filter" className="h-9 w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allEditors")}</SelectItem>
              {(data?.actors || []).map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name} {a.role ? `(${t(a.role)})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={editType} onValueChange={(v) => { setEditType(v); setPage(1); }}>
            <SelectTrigger data-testid="overrides-type-filter" className="h-9 w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allTypes")}</SelectItem>
              <SelectItem value="time">{t("typeTime")}</SelectItem>
              <SelectItem value="location">{t("typeLocation")}</SelectItem>
              <SelectItem value="review">{t("typeReview")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)}
          </div>
        ) : items.length === 0 ? (
          <p data-testid="overrides-empty"
            className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            {t("noOverrides")}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <Table data-testid="overrides-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("when")}</TableHead>
                  <TableHead>{t("supervisor")}</TableHead>
                  <TableHead>{t("staff")}</TableHead>
                  <TableHead>{t("editTypeLabel")}</TableHead>
                  <TableHead>{t("changeCol")}</TableHead>
                  <TableHead>{t("note")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.id} data-testid="override-row">
                    <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums">
                      {fmtKLDateTime(r.at)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {r.actorName}
                      {r.actorRole && (
                        <span className="ml-1 text-xs text-muted-foreground">({t(r.actorRole)})</span>
                      )}
                    </TableCell>
                    <TableCell>{r.staffName}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {r.types.map((ty) => <TypeBadge key={ty} type={ty} t={t} />)}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      <div className="space-y-1">
                        {r.timeAfter && (
                          <p className="flex items-center gap-1 whitespace-nowrap font-mono tabular-nums" data-testid="override-time-change">
                            {fmtKLDateTime(r.timeBefore)}
                            <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                            <span className="font-semibold">{fmtKLDateTime(r.timeAfter)}</span>
                          </p>
                        )}
                        {r.siteAfter && (
                          <p className="flex items-center gap-1" data-testid="override-site-change">
                            <MapPin className="h-3 w-3 shrink-0 text-muted-foreground" />
                            {r.siteBefore || "—"}
                            <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                            <span className="font-semibold">{r.siteAfter}</span>
                          </p>
                        )}
                        {r.decision && (
                          <p className="flex items-center gap-1.5">
                            {decisionBadge(r)}
                            {r.reasonCode && (
                              <span className="text-muted-foreground">{reasonLabel(t, r.reasonCode)}</span>
                            )}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[240px] text-xs">
                      {r.note
                        ? <span data-testid="override-note">{r.note}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <Button size="sm" variant="outline" data-testid="overrides-prev"
              disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              {t("prevPage")}
            </Button>
            <span className="text-xs tabular-nums text-muted-foreground">{page} / {totalPages}</span>
            <Button size="sm" variant="outline" data-testid="overrides-next"
              disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              {t("nextPage")}
            </Button>
          </div>
        )}
          </TabsContent>

          <TabsContent value="roles">
            <RoleChangesTab />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
